import { useEffect, useRef, useState } from 'react';

/** Tempo mínimo na tela, mesmo se a sessão resolver antes disso. */
const MIN_MS = 3_000;
const EXIT_MS = 220;

/**
 * Tela de abertura, uma vez por carregamento do app.
 *
 * O traço se desenha (a estrada) e o ponto pousa no fim dele — é o
 * mesmo gesto do marcador de veículo chegando no mapa, só que em
 * miniatura e na primeira tela que a pessoa vê. Fecha o ciclo: quem
 * abre o app vê a mesma linguagem de movimento que vai ver no mapa.
 *
 * `ready` chega true quando a sessão já resolveu. O componente só some
 * quando as duas coisas acontecerem — sessão pronta E tempo mínimo
 * cumprido — para nunca piscar numa rede rápida nem prender numa lenta
 * além do necessário.
 */
export function Splash({
  appName,
  tagline,
  ready,
  onFinished,
}: {
  appName: string;
  tagline: string;
  ready: boolean;
  onFinished: () => void;
}) {
  const [exiting, setExiting] = useState(false);
  const mountedAt = useRef(Date.now());

  useEffect(() => {
    if (!ready || exiting) return;
    const elapsed = Date.now() - mountedAt.current;
    const wait = Math.max(0, MIN_MS - elapsed);
    const timer = setTimeout(() => setExiting(true), wait);
    return () => clearTimeout(timer);
  }, [ready, exiting]);

  useEffect(() => {
    if (!exiting) return;
    const timer = setTimeout(onFinished, EXIT_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exiting]);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-[linear-gradient(165deg,var(--c-brand-soft),var(--c-brand)_55%,var(--c-brand-deep))] transition-opacity ease-out"
      style={{
        opacity: exiting ? 0 : 1,
        transitionDuration: `${EXIT_MS}ms`,
      }}
      role="status"
      aria-label={`Abrindo ${appName}`}
    >
      <div className="flex flex-col items-center">
        <svg
          // Margem de 8 em volta do desenho original (0 0 120 72): sem
          // ela a borda do ponto final — e mais ainda com o pulso, que
          // escala até 1.18× — passava do viewBox e cortava.
          viewBox="-8 -8 136 88"
          width="128"
          height="83"
          aria-hidden="true"
          className="[animation:splash-fade-in_.5s_ease-out_both]"
        >
          {/* A estrada: curva única, sem cruzamento nem destino marcado —
              o produto ainda não tem rota fixa, tem alcance. */}
          <path
            d="M6 58C28 58 30 22 60 22S92 58 114 58"
            fill="none"
            stroke="var(--c-brand-ink)"
            strokeOpacity="0.22"
            strokeWidth="6"
            strokeLinecap="round"
          />
          <path
            d="M6 58C28 58 30 22 60 22S92 58 114 58"
            fill="none"
            stroke="var(--c-brand-ink)"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray="1"
            pathLength={1}
            style={{
              animation: 'splash-mark 0.85s 0.1s cubic-bezier(.3,.7,.2,1) both',
            }}
          />
          <circle cx="114" cy="58" r="7.5" fill="var(--c-brand-ink)" className="splash-pulse">
            <animate
              attributeName="opacity"
              values="0;0;1"
              keyTimes="0;0.55;1"
              dur="1s"
              begin="0.1s"
              fill="freeze"
            />
          </circle>
        </svg>

        <p
          className="display mt-5 text-3xl text-brand-ink [animation:splash-fade-in_.5s_.35s_ease-out_both]"
          style={{ letterSpacing: '-0.02em' }}
        >
          {appName}
        </p>
        <p className="mt-1.5 text-sm font-medium text-brand-ink/70 [animation:splash-fade-in_.5s_.45s_ease-out_both]">
          {tagline}
        </p>
      </div>
    </div>
  );
}
